import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiCheckCircle, FiAlertCircle, FiWifiOff, FiLogOut } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SectionCard from '../../components/ui/SectionCard';
import LoadingScreen from '../../components/ui/LoadingScreen';
import DynamicQuestionField from '../../components/survey/DynamicQuestionField';
import { getSurveyBySlug } from '../../services/dynamicFormService';
import { buildQuestionsByKey, isQuestionVisible, validateVisibleQuestions } from '../../utils/dynamicForm';
import {
  buildHouseSurveyPayload,
  enqueueHouseSurvey,
  syncQueuedHouseSurveys,
  getQueuedHouseSurveysCount,
  HouseSurveyQueueFullError,
  MAX_QUEUED_HOUSE_SURVEYS,
} from '../../services/houseSurveyService';

/**
 * Formulario genérico para el paso actual del flujo de una vivienda
 * (`GET /v1/houses/:houseId/workflow` → `next_step.survey_slug`), ej.
 * "conversation" después de una visita con `visit_result:
 * 'conversation'`. Carga y renderiza el formulario dinámico
 * correspondiente a `slug`, igual que `HouseRegistrationView` /
 * `VisitCaptureView` pero genérico para cualquier encuesta.
 *
 * El envío (`POST /v1/houses/:houseId/surveys/:surveySlug`) es
 * offline-first: se guarda primero en una cola local
 * (`houseSurveyService`, máx. {@link MAX_QUEUED_HOUSE_SURVEYS} sin
 * sincronizar). Después de guardar, se sincroniza de inmediato y se
 * consulta `GET /v1/houses/:houseId/workflow` para saber cuál es el
 * siguiente formulario pendiente y continuar automáticamente con él
 * ("Guardar y continuar"), sin necesidad de registrar una nueva
 * visita. El botón "Concluir visita" guarda el formulario actual pero
 * regresa a la lista de viviendas en vez de continuar el flujo.
 */
export default function HouseFormStepView() {
  const { houseId, slug } = useParams();
  const navigate = useNavigate();

  const [survey, setSurvey] = useState(undefined); // undefined=cargando, null=no encontrado
  const [loadError, setLoadError] = useState(null);
  const [offline, setOffline] = useState(false);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [submitState, setSubmitState] = useState('idle'); // idle | saving | continuing | done
  const [submitError, setSubmitError] = useState(null);
  const [syncState, setSyncState] = useState('idle'); // idle | syncing | synced | error
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    let active = true;
    setSurvey(undefined);
    setLoadError(null);
    setOffline(!navigator.onLine);
    setAnswers({});
    setErrors({});
    setSubmitState('idle');
    setSubmitError(null);
    setSyncState('idle');

    getSurveyBySlug(slug)
      .then((data) => {
        if (!active) return;
        setSurvey(data);
        if (!navigator.onLine) setOffline(true);
      })
      .catch((err) => {
        if (!active) return;
        setSurvey(null);
        setLoadError(err.message || 'No se pudo cargar el formulario.');
      });

    return () => {
      active = false;
    };
  }, [slug]);

  const questions = useMemo(() => survey?.questions ?? [], [survey]);
  const questionsByKey = useMemo(() => buildQuestionsByKey(questions), [questions]);
  const visibleQuestions = useMemo(
    () => questions.filter((q) => isQuestionVisible(q, answers, questionsByKey)),
    [questions, answers, questionsByKey]
  );

  if (survey === undefined) {
    return <LoadingScreen label="Cargando formulario…" />;
  }

  function handleAnswerChange(question, value) {
    setAnswers((prev) => ({ ...prev, [question.id]: value }));
    setErrors((prev) => {
      if (!prev[question.id]) return prev;
      const next = { ...prev };
      delete next[question.id];
      return next;
    });
  }

  async function handleSubmit(concludeVisit = false) {
    const validationErrors = validateVisibleQuestions(visibleQuestions, answers);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitError(null);
    setSubmitState(concludeVisit ? 'saving' : 'continuing');
    try {
      const answersByKey = questions.reduce((acc, question) => {
        if (visibleQuestions.includes(question)) {
          acc[question.key] = answers[question.id] ?? null;
        }
        return acc;
      }, {});
      const houseSurvey = buildHouseSurveyPayload({ houseId, surveySlug: slug, answersByKey });
      enqueueHouseSurvey(houseSurvey);

      if (concludeVisit) {
        // "Concluir visita": guarda este paso y regresa a la lista de
        // viviendas sin continuar el flujo. La sincronización se
        // intenta en segundo plano, sin bloquear al operativo.
        attemptBackgroundSync();
        navigate('/data-entry/houses');
        return;
      }

      // "Guardar": guarda y sincroniza en segundo plano, luego regresa
      // a la lista de formularios de la visita para que el usuario elija el siguiente.
      attemptBackgroundSync();
      navigate(`/data-entry/houses/${houseId}/forms`, { replace: true });
      return;
    } catch (err) {
      setSubmitState('idle');
      setSubmitError(
        err instanceof HouseSurveyQueueFullError
          ? err.message
          : err.message || 'No se pudo guardar la información en este dispositivo.'
      );
    }
  }

  async function attemptBackgroundSync() {
    setSyncState('syncing');
    try {
      await syncQueuedHouseSurveys();
      setSyncState('synced');
    } catch {
      setSyncState('error');
    } finally {
      setPendingCount(getQueuedHouseSurveysCount());
    }
  }

  if (!survey) {
    return (
      <DashboardLayout title="Formulario" subtitle="No encontrado">
        {loadError && (
          <div className="form-error" role="alert">
            <FiAlertCircle /> {loadError}
          </div>
        )}
        <SectionCard>
          <p>El formulario solicitado no existe o fue eliminado.</p>
        </SectionCard>
        <Link to={`/data-entry/houses/${houseId}/forms`} className="btn btn-outline btn-block">
          <FiArrowLeft /> Volver a formularios de la visita
        </Link>
      </DashboardLayout>
    );
  }

  if (submitState === 'done') {
    return (
      <DashboardLayout title="¡Listo!" subtitle={survey.label}>
        <SectionCard>
          <div className="survey-success">
            <FiCheckCircle className="survey-success-icon" />
            <p className="empty-state__title">Información guardada</p>
            <p className="empty-state__description">
              {syncState === 'synced' && 'La información ya se sincronizó con el servidor.'}
              {syncState === 'syncing' && 'Sincronizando con el servidor…'}
              {syncState === 'error' &&
                `Se guardó en este dispositivo. Aún no hay conexión para sincronizar (${pendingCount}/${MAX_QUEUED_HOUSE_SURVEYS} pendientes).`}
              {syncState === 'idle' && 'La información quedó guardada en este dispositivo.'}
            </p>
          </div>
        </SectionCard>
        <Link to="/data-entry/houses" className="btn btn-primary btn-block">
          Volver a viviendas
        </Link>
        <Link to={`/data-entry/houses/${houseId}/forms`} className="btn btn-outline btn-block">
          <FiArrowLeft /> Volver a formularios
        </Link>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={survey.label} subtitle={survey.description}>
      <button
        type="button"
        className="form-back-btn"
        onClick={() => navigate(`/data-entry/houses/${houseId}/forms`)}
        aria-label="Volver a formularios"
        title="Volver a formularios"
      >
        <FiArrowLeft />
      </button>

      {offline && (
        <div className="form-error form-error--muted" role="status">
          <FiWifiOff /> Sin conexión: mostrando una copia guardada de este formulario.
        </div>
      )}

      {submitError && (
        <div className="form-error" role="alert">
          <FiAlertCircle /> {submitError}
        </div>
      )}

      {questions.length === 0 ? (
        <SectionCard>
          <p>Este formulario todavía no tiene preguntas configuradas.</p>
        </SectionCard>
      ) : (
        <SectionCard>
          {visibleQuestions.map((question) => (
            <DynamicQuestionField
              key={question.id}
              question={question}
              value={answers[question.id]}
              error={errors[question.id]}
              onChange={(value) => handleAnswerChange(question, value)}
            />
          ))}
        </SectionCard>
      )}

      <div className="survey-nav">
        <button
          type="button"
          className="btn btn-primary btn-block"
          onClick={() => handleSubmit(false)}
          disabled={submitState === 'continuing' || submitState === 'saving'}
        >
          {submitState === 'continuing' ? 'Guardando…' : (
            <>
              <FiCheckCircle /> Guardar
            </>
          )}
        </button>
        <button
          type="button"
          className="btn btn-outline btn-block"
          onClick={() => handleSubmit(true)}
          disabled={submitState === 'continuing' || submitState === 'saving'}
        >
          {submitState === 'saving' ? 'Guardando…' : (
            <>
              <FiLogOut /> Concluir visita
            </>
          )}
        </button>
      </div>
    </DashboardLayout>
  );
}
